/**
 * The app's single, fixed time zone (UTC+3, no DST): every time shown or
 * entered anywhere in the frontend is read as this zone's wall-clock time,
 * whatever the browser's own zone is. Values still travel to and from the
 * backend as timezone-aware ISO instants (`toISOString()` / `new Date(iso)`);
 * only the wall-clock side — formatting, "start of day", `datetime-local`
 * inputs, the schedule grid — goes through here.
 *
 * Because the offset is fixed, day arithmetic is plain millisecond math
 * (a day is always 24h) and the helpers below shift by the offset instead of
 * relying on the host's local-time getters.
 */

/** IANA zone for `Intl.DateTimeFormat`'s `timeZone` option. Note the inverted sign: `Etc/GMT-3` is UTC+3. */
export const APP_TIME_ZONE = 'Etc/GMT-3'

const APP_OFFSET_MS = 3 * 60 * 60 * 1000

export interface AppDateParts {
  // Calendar fields of an instant, in the app time zone.

  year: number
  month: number
  // 0-11, as `Date#getMonth`.

  day: number
  // Day of the month, 1-31.

  hour: number
  minute: number
  weekday: number
  // 0 = Sunday .. 6 = Saturday, as `Date#getDay`.
}

/** Calendar fields of `date` as seen in the app time zone. */
export function appParts(date: Date): AppDateParts {
  const shifted = new Date(date.getTime() + APP_OFFSET_MS)
  return {
    year: shifted.getUTCFullYear(),
    month: shifted.getUTCMonth(),
    day: shifted.getUTCDate(),
    hour: shifted.getUTCHours(),
    minute: shifted.getUTCMinutes(),
    weekday: shifted.getUTCDay(),
  }
}

/** The instant at the given wall-clock time in the app time zone (fields may overflow, as with `Date.UTC`). */
export function appDate(year: number, month: number, day: number, hour = 0, minute = 0): Date {
  return new Date(Date.UTC(year, month, day, hour, minute) - APP_OFFSET_MS)
}

const pad = (n: number) => String(n).padStart(2, '0')

/** "HH:MM" of `date` in the app time zone, 24h. */
export function appTimeOfDay(date: Date): string {
  const p = appParts(date)
  return `${pad(p.hour)}:${pad(p.minute)}`
}

/** "YYYY-MM-DD" of `date` in the app time zone. */
export function appDateString(date: Date): string {
  const p = appParts(date)
  return `${p.year}-${pad(p.month + 1)}-${pad(p.day)}`
}

// ── Recurring slots (group schedules) ──────────────────────────────────────
// A group's schedule slot is a bare weekday + "HH:MM:SS" with no zone, and the
// backend generates the group's bookings reading those as UTC. The app shows
// and takes them in UTC+3, so slots are converted at the API boundary (see
// `api/groups.ts`): +3h when read, -3h when written. A shift past midnight
// also moves the ISO weekday (1 = Monday .. 7 = Sunday).

const MINUTES_PER_DAY = 24 * 60
const APP_OFFSET_MINUTES = APP_OFFSET_MS / 60_000

function toMinutes(time: string): number {
  const [h = 0, m = 0] = time.split(':').map(Number)
  return h * 60 + m
}

function fromMinutes(total: number): string {
  const wrapped = ((total % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY
  return `${pad(Math.floor(wrapped / 60))}:${pad(wrapped % 60)}:00`
}

export interface SlotTimes {
  // A weekly slot's weekday and wall-clock range.

  day_of_week: number
  // ISO weekday, 1-7.

  start_time: string
  // "HH:MM" or "HH:MM:SS".

  end_time: string
  // "HH:MM" or "HH:MM:SS".
}

/** Whole days a backend (UTC) slot starting at `startTime` moves forward when shown in the app zone: 0 or 1. */
export function backendSlotDayShift(startTime: string): number {
  return Math.floor((toMinutes(startTime) + APP_OFFSET_MINUTES) / MINUTES_PER_DAY)
}

function shiftSlot(slot: SlotTimes, deltaMinutes: number): SlotTimes {
  const start = toMinutes(slot.start_time) + deltaMinutes
  const dayShift = Math.floor(start / MINUTES_PER_DAY)
  return {
    day_of_week: ((((slot.day_of_week - 1 + dayShift) % 7) + 7) % 7) + 1,
    start_time: fromMinutes(start),
    end_time: fromMinutes(toMinutes(slot.end_time) + deltaMinutes),
  }
}

/** A slot as stored by the backend (UTC) → as shown in the app (UTC+3). */
export function slotFromBackend(slot: SlotTimes): SlotTimes {
  return shiftSlot(slot, APP_OFFSET_MINUTES)
}

/** A slot entered in the app (UTC+3) → as the backend stores it (UTC). */
export function slotToBackend(slot: SlotTimes): SlotTimes {
  return shiftSlot(slot, -APP_OFFSET_MINUTES)
}

/**
 * True when a UTC+3 slot would cross 00:00 UTC (03:00 in the app zone) — the
 * backend slot would then span two weekdays, which one weekly row can't
 * express. Such a slot isn't accepted by the form.
 */
export function slotStraddlesBackendMidnight(startTime: string, endTime: string): boolean {
  return toMinutes(startTime) < APP_OFFSET_MINUTES && toMinutes(endTime) >= APP_OFFSET_MINUTES
}
